import { useController } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";

/**
 * A controlled checkbox component that integrates with React Hook Form using the `useController` hook.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} [props.name=""] - The name of the checkbox field, used by React Hook Form.
 * @param {Object} [props.formControl={}] - The `control` object from React Hook Form, typically provided by `useForm`.
 * @param {string} [props.label=""] - The label text displayed next to the checkbox.
 * @param {Object} [props.rules={}] - Validation rules for the checkbox, passed to React Hook Form's `useController`.
 * @param {Function} [props.handleChange] - Optional custom handler for the `onChange` event.
 * @param {boolean} [props.value] - The current value of the checkbox (checked or unchecked state).
 * @param {boolean} [props.disabled] - If `true`, the checkbox will be disabled.
 * @param {React.ReactNode} [props.icon] - Icon to display when the checkbox is unchecked.
 * @param {React.ReactNode} [props.checkedIcon] - Icon to display when the checkbox is checked.
 * @param {Object} [props.style] - Additional custom styles applied to the checkbox.
 * @param {string} [props.size="small"] - The size of the checkbox ("small", "medium", "large").
 *
 * @returns {JSX.Element}
 */

export function MiControlledCheckBox({
  name = "",
  formControl: control = {},
  label = "",
  rules = {},
  labelPlacement = "end",
  handleChange,
  value,
  disabled,
  icon,
  checkedIcon,
  readOnly,
  style,
  size = "small",
}) {
  const { classes } = useStyles({ disabled });
  const {
    field: { value: defaultValue, onChange, onBlur },
  } = useController({ name, control, rules });

  return (
    <FormControlLabel
      className={classes.checkbox}
      control={
        <Checkbox
          checked={value || defaultValue}
          onChange={handleChange || onChange}
          onBlur={onBlur}
          disabled={disabled}
          icon={icon}
          size={size}
          checkedIcon={checkedIcon}
          data-testid={`mi-checkbox-${name}`}
          style={style}
          readOnly={readOnly}
          slotProps={{
            input: {
              readOnly,
            },
          }}
        />
      }
      label={<Typography variant="body2">{label}</Typography>}
      labelPlacement={labelPlacement}
    />
  );
}

const useStyles = makeStyles()((_, { disabled }) => ({
  checkbox: { margin: 0, opacity: disabled ? 0.5 : 1 },
}));
